import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../../core/api/ajs-response';

/**
 * ZendeskSsoService — calls the Rails proxy route for Zendesk JWT SSO.
 *
 * Flow (DEV-11861):
 *  - On login-page load the component calls `createSession(return_to)` with no
 *    credentials; Rails authenticates via the existing b2b session cookie
 *    (the seamless / AC3 path).
 *  - On form submit the component calls `createSession(return_to, credentials)`.
 *
 * `return_to` is the deep link Zendesk attached to our `/zendesk/login` URL.
 * It is forwarded to Java, which validates it against the DB allowlist before
 * issuing a JWT — so the destination is server-validated, not trusted from the
 * client. Rails translates the body into the `x-acme-email` / `x-acme-password`
 * headers and the `return_to` query param the Java
 * `/v2/customer/zendesk/sso/session` endpoint expects, and returns Java's
 * success body in the standard `{ success, session }` envelope the other
 * session endpoints use. The component assembles the signed-JWT Zendesk URL
 * from the session's `{ returnTo, jwt }` (Java's authoritative response
 * values, not the raw inbound param).
 */
export interface ZendeskSsoCredentials {
  email: string;
  password: string;
}

/**
 * Java SSO success body (the `session` of the standard envelope). Only
 * `returnTo` and `jwt` are consumed; the rest (roles, mid, etc.) is ignored.
 */
export interface ZendeskSsoSession {
  /** Server-validated Zendesk return origin (e.g. `https://acme.zendesk.com`). */
  returnTo: string;
  /** Short-lived signed JWT used to start the Zendesk session. */
  jwt: string;
}

/** Standard `{ success, session }` envelope returned by the Rails proxy. */
export interface ZendeskSsoSessionResponse {
  success: boolean;
  session: ZendeskSsoSession;
}

@Injectable({ providedIn: 'root' })
export class ZendeskSsoService {
  private readonly http = inject(HttpClient);

  /**
   * Request a Zendesk SSO session.
   *
   * @param returnTo The deep link Zendesk attached to the login URL. Forwarded
   *   to Java for allowlist validation; required on both paths.
   * @param credentials Omit for the cookie-only seamless attempt; provide on
   *   form submit. With no credentials Rails falls back to the session cookie.
   */
  createSession(
    returnTo: string | null,
    credentials?: ZendeskSsoCredentials,
  ): Promise<AjsResponse<ZendeskSsoSessionResponse>> {
    return wrapAjs(
      this.http.post<ZendeskSsoSessionResponse>('/v2/api/customer/sso/session', {
        return_to: returnTo,
        ...(credentials ?? {}),
      }),
    );
  }
}
